import React from "react";
import { Box, Chip, Stack } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

// How well the grading knows a color, from its record on the GIA certified stones (the worker's
// ColorAnalysis.Grading State, its parts comma-joined): whether its grades can be trusted, need
// more GIA gradings to be sure, or call for a look at the pictures.
const STATES: Record<
  string,
  {
    label: string;
    color: "success" | "error" | "default";
    variant: "outlined" | "filled";
    icon: React.ReactElement;
  }
> = {
  populated: {
    label: "Reliable",
    color: "success",
    variant: "outlined",
    icon: <CheckCircleIcon />,
  },
  "few examples": {
    label: "Needs more gradings",
    color: "default",
    variant: "outlined",
    icon: <HourglassEmptyIcon />,
  },
  incoherent: {
    label: "Check pictures",
    color: "error",
    variant: "outlined",
    icon: <ErrorIcon />,
  },
  "no certified example": {
    label: "No GIA example",
    color: "default",
    variant: "outlined",
    icon: <HelpOutlineIcon />,
  },
};

// Compact, in a list of colors: each chip shrinks to its circled icon, and pointing at it lays the
// whole chip over it, above the page, so the list around it never moves.
const iconOnlySx = {
  "& .MuiChip-label": { display: "none" },
  "& .MuiChip-icon": { mx: "2px" },
};
const overlaySx = {
  position: "absolute",
  top: 0,
  left: 0,
  zIndex: 1,
  visibility: "hidden",
  bgcolor: "background.paper",
  // As wide as its words: a chip is otherwise held to its parent's width, here the icon's.
  width: "max-content",
  maxWidth: "none",
};

const StateChip = ({ part, sx }: { part: string; sx?: object }) => (
  <Chip
    size="small"
    label={STATES[part].label}
    color={STATES[part].color}
    variant={STATES[part].variant}
    icon={STATES[part].icon}
    sx={sx}
  />
);

const ColorStateChips = ({
  state,
  compact = false,
}: {
  state?: string | null;
  compact?: boolean;
}) => {
  if (!state) return null;
  const parts = state.split(", ").filter((part) => STATES[part]);
  return (
    <Stack direction="row" spacing={0.5}>
      {parts.map((part) =>
        compact ? (
          <Box
            key={part}
            sx={{
              // Flex, so the chip is not set on a line of text a little below the top the
              // overlay is laid from.
              display: "flex",
              position: "relative",
              "&:hover > .MuiChip-root:last-of-type": { visibility: "visible" },
            }}
          >
            <StateChip part={part} sx={iconOnlySx} />
            <StateChip part={part} sx={overlaySx} />
          </Box>
        ) : (
          <StateChip key={part} part={part} />
        ),
      )}
    </Stack>
  );
};

export default ColorStateChips;
